import React, { useState, useEffect, useRef } from 'react';
import styled, { withTheme } from 'styled-components';

import MapNavButton from '@ui/buttons/MapNavButton.tsx';
import Tooltip from '@ui/tooltip';
import { getThemeVal } from '@utils/styled-component-tools';

import useComponentFocused from '../../../../src/hooks/useComponentFocused.tsx';

const StyledMapNavButton = styled(MapNavButton)`
  & svg {
    fill: ${getThemeVal('colors.background', '#FFFFFF')};
  }
  &.showPanControls {
    opacity: 1;
  }
`;
const Anchor = styled.div`
  position: relative;
`;
const PanControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  inset-inline-end: 36px;
  bottom: -56px;
  background-color: var(--color-background-50);
  border: 1px solid ${getThemeVal('colors.primaryText', '#333333')};
  border-radius: ${getThemeVal('radii.md', '4px')};
  padding: 8px;
`;
const TopWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;
const BottomWrapper = styled.div`
  display: flex;
`;
const Space = styled.div`
  width: 10px;
`;

const MapPanControlsDesktop = withTheme(({ bus, T, delta = 150 }) => {
  const [showPanControls, setShowPanControls] = useState(false);
  const [treatAsClick, setTreatAsClick] = useState(true);

  const { ref: componentFocusedRef, isComponentFocused, setIsComponentFocused } = useComponentFocused(true);

  const panControlsWrapperRef = useRef();

  useEffect(() => {
    if (!isComponentFocused && showPanControls) {
      setShowPanControls(false);
    } // if the outside click was detected, but menu was open, hide it
  }, [isComponentFocused]);

  const onPanTriggerClick = async () => {
    setShowPanControls(prev => !prev);
    setIsComponentFocused(!showPanControls);
  };

  const onPanTriggerKeydown = e => {
    if (e.shiftKey && e.key === 'Tab') {
      setShowPanControls(false);
    }
  };

  const mapMoveAction = move => {
    switch (move) {
      case 'panUp':
        bus.send('map/pan', { offset: [0, -1 * delta], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panUp',
        });
        break;
      case 'panDown':
        bus.send('map/pan', { offset: [0, delta], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panDown',
        });
        break;
      case 'panLeft':
        bus.send('map/pan', { offset: [-1 * delta, 0], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panLeft',
        });
        break;
      case 'panRight':
        bus.send('map/pan', { offset: [delta, 0], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panRight',
        });
    }
  };

  const panButtonOnClick = move => {
    if (treatAsClick) {
      mapMoveAction(move);
    }
  };

  const intervalIdRef = useRef(null);

  const handleMouseDown = move => {
    setTreatAsClick(false);
    intervalIdRef.current = setInterval(() => {
      mapMoveAction(move);
    }, 50);
  };

  const handleMouseUp = () => {
    setTreatAsClick(true);
    clearInterval(intervalIdRef.current);
    intervalIdRef.current = null;
  };

  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const triggerRef = useRef(null);
  const firstControlRef = useRef(null);
  const lastControlRef = useRef(null);

  const wrapperKeyDownHandler = e => {
    if (e.key === 'Escape') {
      setShowPanControls(false);
      setIsComponentFocused(false);
      triggerRef.current.focus();
    }
    if (e.key === 'Tab' && !e.shiftKey) {
      if (document.activeElement === lastControlRef.current) {
        setShowPanControls(false);
      }
    }
  };

  return (
    <>
      {!showPanControls ? (
        <Tooltip
          text={T('mapPanControls:Show Panning Controls')}
          arrowPosition="left"
          data-testid="MapPanTooltip"
          cancelTooltip={showPanControls}
        >
          <Anchor ref={componentFocusedRef} tabIndex={-1}>
            <StyledMapNavButton
              onKeyDown={e => onPanTriggerKeydown(e)}
              ref={triggerRef}
              data-testid="MapPanTrigger"
              iconName="move.note"
              onClick={onPanTriggerClick}
              aria-label={T('mapPanControls:Show Panning Controls')}
              aria-pressed={showPanControls}
              /* Only set aria-controls when the controlled element is present in the DOM to satisfy WCAG */
              aria-controls={showPanControls ? 'mapPanControls' : undefined}
              aria-expanded={showPanControls}
              className={showPanControls ? 'showPanControls' : ''}
            />
          </Anchor>
        </Tooltip>
      ) : (
        <Anchor ref={componentFocusedRef} tabIndex={-1}>
          <StyledMapNavButton
            onKeyDown={e => onPanTriggerKeydown(e)}
            ref={triggerRef}
            data-testid="MapPanTrigger"
            iconName="move.note"
            onClick={onPanTriggerClick}
            aria-label={T('mapPanControls:Show Panning Controls')}
            aria-pressed={showPanControls}
            /* Only set aria-controls when the controlled element is present in the DOM to satisfy WCAG */
            aria-controls={showPanControls ? 'mapPanControls' : undefined}
            aria-expanded={showPanControls}
            className={showPanControls ? 'showPanControls' : ''}
          />
          {showPanControls && (
            <PanControlsWrapper
              role="dialog"
              aria-modal="false"
              aria-label={`${T('mapPanControls:Move Map')} dialog`}
              id={'mapPanControls'}
              data-testid="MapPanControls"
              ref={panControlsWrapperRef}
              onKeyDown={wrapperKeyDownHandler}
            >
              <TopWrapper>
                <Tooltip isVisible="false" arrowPosition={'right'} text={T('mapPanControls:Pan Map Up')}>
                  <StyledMapNavButton
                    ref={firstControlRef}
                    data-testid="desktopPanUp"
                    iconName="arrow.down"
                    degreesOfIconRotation={180}
                    aria-label={T('mapPanControls:Pan Map Up')}
                    onClick={() => panButtonOnClick('panUp')}
                    onMouseDown={() => handleMouseDown('panUp')}
                    onMouseUp={handleMouseUp}
                    onMouseOut={handleMouseUp}
                  />
                </Tooltip>
              </TopWrapper>
              <BottomWrapper>
                <Tooltip isVisible="false" arrowPosition={'top'} text={T('mapPanControls:Pan Map Left')}>
                  <StyledMapNavButton
                    data-testid="desktopPanLeft"
                    iconName="arrow.down"
                    degreesOfIconRotation={90}
                    aria-label={T('mapPanControls:Pan Map Left')}
                    onClick={() => panButtonOnClick('panLeft')}
                    onMouseDown={() => handleMouseDown('panLeft')}
                    onMouseUp={handleMouseUp}
                    onMouseOut={handleMouseUp}
                  />
                </Tooltip>
                <Space />
                <Tooltip isVisible="false" arrowPosition={'top'} text={T('mapPanControls:Pan Map Down')}>
                  <StyledMapNavButton
                    data-testid="desktopPanDown"
                    iconName="arrow.down"
                    degreesOfIconRotation={0}
                    aria-label={T('mapPanControls:Pan Map Down')}
                    onClick={() => panButtonOnClick('panDown')}
                    onMouseDown={() => handleMouseDown('panDown')}
                    onMouseUp={handleMouseUp}
                    onMouseOut={handleMouseUp}
                  />
                </Tooltip>
                <Space />
                <Tooltip isVisible="false" arrowPosition={'top'} text={T('mapPanControls:Pan Map Right')}>
                  <StyledMapNavButton
                    ref={lastControlRef}
                    data-testid="desktopPanRight"
                    iconName="arrow.down"
                    degreesOfIconRotation={270}
                    aria-label={T('mapPanControls:Pan Map Right')}
                    onClick={() => panButtonOnClick('panRight')}
                    onMouseDown={() => handleMouseDown('panRight')}
                    onMouseUp={handleMouseUp}
                    onMouseOut={handleMouseUp}
                  />
                </Tooltip>
              </BottomWrapper>
            </PanControlsWrapper>
          )}
        </Anchor>
      )}
    </>
  );
});

export default MapPanControlsDesktop;
